import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight, ShieldCheck, FileText, HardHat } from "lucide-react";
import { MaskedLines } from "./shared";
import { PHONE_DISPLAY, PHONE_TEL } from "./Nav";

const HERO_IMG =
  "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85";

const TRUST = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: FileText, label: "Free Written Estimates" },
  { icon: HardHat, label: "Owner On Every Job" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const frameY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden blueprint-grid pt-24 pb-16">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(204,85,0,0.09),transparent_55%)]" aria-hidden="true" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-safety font-bold mb-8"
          >
            <span className="w-8 h-[2px] bg-safety" aria-hidden="true" />
            TCB Properties, LLC — Residential Construction
          </motion.div>

          <h1 className="font-display uppercase leading-[0.92] tracking-tight text-[clamp(3.2rem,9vw,7.5rem)] text-bone">
            <MaskedLines lines={["We build every home", <>like it&apos;s our <span className="text-safety">own.</span></>]} delay={0.3} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="text-steel text-base sm:text-lg leading-relaxed mt-8 max-w-xl"
          >
            Remodels, decks, additions, fencing, roofing, tree work and handyman
            services — one local crew for every trade. Straight answers, written
            estimates, and a site left cleaner than we found it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              href="#quote"
              data-testid="hero-request-quote-btn"
              className="group flex items-center gap-2 bg-safety hover:bg-safety-hover text-ink font-bold text-base px-8 py-4 clip-notch-sm transition-all duration-200 hover:-translate-y-1"
            >
              Request a Free Quote
              <ArrowDownRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:translate-y-0.5" strokeWidth={2.5} />
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-btn"
              className="flex items-center gap-2 border border-line hover:border-safety text-bone font-semibold text-base px-8 py-4 clip-notch-sm transition-all duration-200 hover:-translate-y-1"
            >
              <Phone className="w-5 h-5 text-safety" strokeWidth={2.5} />
              Call {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-wrap gap-x-8 gap-y-3 mt-12"
          >
            {TRUST.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm text-steel">
                <Icon className="w-4 h-4 text-safety" strokeWidth={2.5} />
                <span className="font-semibold">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div style={{ y: frameY }} className="absolute -top-8 -left-8 w-full h-full border border-safety/40" aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative clip-notch"
          >
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMG}
              alt="TCB crew framing a residential addition"
              data-testid="hero-image"
              className="w-full h-[560px] object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" aria-hidden="true" />
          </motion.div>
          <motion.div
            style={{ y: frameY }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            className="absolute -bottom-6 -left-10 bg-ink-card border border-line px-6 py-4 clip-notch-sm"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety font-bold">Est. turnaround</p>
            <p className="font-cond font-bold text-2xl uppercase text-bone mt-1">Quote in 24 hours</p>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[3px] hazard-stripes opacity-60" aria-hidden="true" />
    </section>
  );
}
